import React from 'react';
import './public/site.css';
import PublicHeader from './public/PublicHeader';
import PublicFooter from './public/PublicFooter';

const TermsEN: React.FC = () => {
  React.useEffect(() => {
    document.title = 'Terms of Use - Fconnect';
  }, []);
  return (
    <div className="home-page fc-site">
      <PublicHeader lang="en" />

      {/* Content */}
      <section className="home-container">
        <header className="terms-header">
          <h1>Terms of Use</h1>
          <p className="last-updated">Last updated: {new Date().toLocaleDateString('en-US')}</p>
        </header>
        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              Welcome to Fconnect! By accessing or using our services, you agree to be bound by these
              Terms. If you do not agree to any part of these Terms, please do not use our services.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Definitions</h2>
            <ul>
              <li><strong>"Services"</strong> means the Fconnect platform, including the website, mobile apps, and related services</li>
              <li><strong>"User"</strong> means any individual or organization using our Services</li>
              <li><strong>"Provider"</strong> means individuals or businesses providing services via the platform</li>
              <li><strong>"Customer"</strong> means users who book and pay for services</li>
              <li><strong>"Content"</strong> means text, images, videos, audio and other materials shared on the platform</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. User Accounts</h2>
            <h3>3.1 Registration</h3>
            <ul>
              <li>You must provide accurate, complete, and up‑to‑date information during registration</li>
              <li>You are responsible for keeping your login credentials secure</li>
              <li>You must notify us immediately of any unauthorized use of your account</li>
              <li>Each person may only create a single account</li>
            </ul>

            <h3>3.2 Age requirements</h3>
            <p>
              You must be at least 18 years old to use the Services. If you are under 18, you must have
              the consent and supervision of a parent or legal guardian.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Use of Services</h2>
            <h3>4.1 Rules of use</h3>
            <p>When using our Services, you agree to:</p>
            <ul>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not use the Services for unlawful or fraudulent purposes</li>
              <li>Not disrupt or damage the operation of our systems</li>
              <li>Not create or transmit harmful, spam, or malicious content</li>
              <li>Respect the privacy and intellectual property of others</li>
            </ul>

            <h3>4.2 Prohibited conduct</h3>
            <p>The following activities are prohibited:</p>
            <ul>
              <li>Using false information or impersonating others</li>
              <li>Posting pornographic, violent, or illegal content</li>
              <li>Harassing, threatening, or abusing other users</li>
              <li>Manipulating reviews or ratings</li>
              <li>Using unauthorized bots, scripts, or automation tools</li>
              <li>Collecting other users' personal information</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Services and Payments</h2>
            <h3>5.1 Booking</h3>
            <ul>
              <li>Customers can place bookings on our platform</li>
              <li>All bookings must be confirmed by the Provider</li>
              <li>Prices and conditions are determined by Providers</li>
              <li>We are not responsible for the quality of services provided</li>
            </ul>

            <h3>5.2 Payment</h3>
            <ul>
              <li>Payments are made via accepted methods on the platform</li>
              <li>We may charge service fees and commissions as specified</li>
              <li>All paid fees are non‑refundable unless otherwise stated</li>
              <li>You are responsible for all charges arising from use of your account</li>
            </ul>

            <h3>5.3 Cancellation and refunds</h3>
            <ul>
              <li>Cancellation and refund policies are determined by each Provider</li>
              <li>In case of disputes, we will assist per our policies</li>
              <li>Refunds (if any) will be processed within 7–14 business days</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>6. Content and Intellectual Property</h2>
            <h3>6.1 Your content</h3>
            <ul>
              <li>You retain ownership of content you create and share</li>
              <li>By posting, you grant us rights to use the content to operate the Services</li>
              <li>You are responsible for the accuracy and legality of your content</li>
              <li>We may remove content that violates these Terms</li>
            </ul>

            <h3>6.2 Our content</h3>
            <ul>
              <li>All platform content (except user‑generated content) is our property</li>
              <li>You may not copy, distribute, or use such content without permission</li>
              <li>Logos, trademarks, and the Fconnect name are our intellectual property</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>7. Privacy</h2>
            <p>
              Collection, use, and protection of your personal information is governed by our
              <a href="/en/privacy" target="_blank" rel="noopener noreferrer"> Privacy Policy</a>.
              By using the Services, you agree to the terms of that policy.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Liability and Disclaimers</h2>
            <h3>8.1 Disclaimer</h3>
            <ul>
              <li>The Services are provided "as is" without warranties of any kind</li>
              <li>We do not warrant continuous availability, security, or error‑free operation</li>
              <li>We are not responsible for the conduct or content of other users</li>
              <li>We do not guarantee the quality of services provided by Providers</li>
            </ul>

            <h3>8.2 Limitation of liability</h3>
            <ul>
              <li>We are not liable for indirect, incidental, or consequential damages</li>
              <li>Our total liability will not exceed the amounts you paid in the last 12 months</li>
              <li>Some jurisdictions do not allow limitations, so these may not apply to you</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>9. Termination</h2>
            <h3>9.1 By you</h3>
            <p>You may stop using the Services and delete your account at any time via account settings.</p>

            <h3>9.2 By us</h3>
            <p>We may suspend or terminate your account if:</p>
            <ul>
              <li>You violate these Terms</li>
              <li>We suspect fraudulent or unlawful activity</li>
              <li>Required by law</li>
              <li>For business or technical reasons</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>10. Dispute Resolution</h2>
            <h3>10.1 Negotiation</h3>
            <p>We encourage resolving disputes through direct negotiation before taking legal action.</p>

            <h3>10.2 Arbitration</h3>
            <p>If negotiation fails, disputes will be resolved by arbitration under applicable law.</p>

            <h3>10.3 Governing law</h3>
            <p>These Terms are governed by the laws of Vietnam and you consent to its courts' jurisdiction.</p>
          </section>

          <section className="terms-section">
            <h2>11. Changes to the Terms</h2>
            <p>
              We may update these Terms from time to time. For material changes, we will notify you via
              email or in‑app notifications. Continued use after changes means you accept the new Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. General</h2>
            <ul>
              <li><strong>Severability:</strong> If any provision is invalid, the rest remain in effect</li>
              <li><strong>No waiver:</strong> Failure to enforce a provision is not a waiver</li>
              <li><strong>Assignment:</strong> You may not assign rights or obligations without our consent</li>
              <li><strong>Entire agreement:</strong> These Terms and the Privacy Policy form the entire agreement</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>13. Contact</h2>
            <p>If you have any questions about these Terms, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> trile@fconnect.vn</p>
              <p><strong>Phone:</strong> 02513684266</p>
              <p><strong>Address:</strong> 11/4C, To 15, Kp 6, Nguyen Ai Quoc Street, Tran Bien Ward, Dong Nai City, Vietnam</p>
              <p><strong>Support hours:</strong> 8:00 - 22:00 (Mon - Sun)</p>
            </div>
          </section>
        </div>
      </section>

      <PublicFooter lang="en" />
    </div>
  );
};

export default TermsEN;


